import React, { useState, useEffect } from 'react';
import SortableHeader from './SortableHeader';
import { useTableSort } from '../utils/tableSort';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,

} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { MaterialNorm, MaterialNormCreate, MaterialType } from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import { useToast } from './ToastProvider';
import { apiErrorMessage } from '../utils/apiError';

interface MaterialNormsTableProps {
  userRole: string;
}

const MaterialNormsTable: React.FC<MaterialNormsTableProps> = ({ userRole }) => {
  const [materialNorms, setMaterialNorms] = useState<MaterialNorm[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toastError } = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editItem, setEditItem] = useState<MaterialNorm | null>(null);

  const [formName, setFormName] = useState('');
  const [formMaterialType, setFormMaterialType] = useState<MaterialType>('EDELSTAHL');

  const isKalotte = userRole === 'KALOTTE';

  useEffect(() => {
    loadMaterialNorms();
  }, []);

  const loadMaterialNorms = async () => {
    try {
      setLoading(true);
      const items = await apiService.getMaterialNorms();
      setMaterialNorms(items);
      setError(null);
    } catch (err) {
      console.error('Error loading material norms:', err);
      setError('Fehler beim Laden der Werkstoffnormen');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mode: 'create' | 'edit', item?: MaterialNorm) => {
    setDialogMode(mode);
    setEditItem(item || null);
    setFormName(item?.name || '');
    setFormMaterialType(item?.material_type || 'EDELSTAHL');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditItem(null);
    setFormName('');
    setFormMaterialType('EDELSTAHL');
  };

  const handleSave = async () => {
    try {
      const data: MaterialNormCreate = {
        name: formName,
        material_type: formMaterialType,
      };

      if (dialogMode === 'create') {
        await apiService.createMaterialNorm(data);
      } else if (editItem) {
        await apiService.updateMaterialNorm(editItem.id, data);
      }

      handleCloseDialog();
      await loadMaterialNorms();
      setError(null);
    } catch (err: any) {
      console.error('Error saving material norm:', err);
      toastError(apiErrorMessage(err, 'Fehler beim Speichern der Werkstoffnorm'));
    }
  };

  const handleDelete = async (item: MaterialNorm) => {
    if (window.confirm(`Soll "${item.name}" wirklich gelöscht werden?`)) {
      try {
        await apiService.deleteMaterialNorm(item.id);
        await loadMaterialNorms();
        setError(null);
      } catch (err: any) {
        console.error('Error deleting material norm:', err);
        toastError(apiErrorMessage(err, 'Fehler beim Löschen der Werkstoffnorm'));
      }
    }
  };

  const filteredItems = searchTerm
    ? materialNorms.filter((item) => {
        const term = searchTerm.toLowerCase();
        return item.name.toLowerCase().includes(term);
      })
    : materialNorms;
  const { sort, order, onSort, sortRows } = useTableSort<'name' | 'material_type' | 'created_at'>(
    'name',
    'asc',
    ['created_at']
  );
  const visibleItems = sortRows(filteredItems, {
    name: (item) => item.name,
    material_type: (item) => item.material_type,
    created_at: (item) => item.created_at,
  });

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Werkstoffnormen ({filteredItems.length}
          {searchTerm ? ` / ${materialNorms.length}` : ''})
        </Typography>
        {isKalotte && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('create')}
          >
            Neue Werkstoffnorm
          </Button>
        )}
      </Box>

      <TextField
        size="small"
        placeholder="Suche nach Name..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2, width: { xs: '100%', sm: 400 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: searchTerm ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchTerm('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', py: 0.75 } }}>
            <TableHead>
              <TableRow>
                <SortableHeader column="name" label="Name" sort={sort} order={order} onSort={onSort} />
                <SortableHeader column="material_type" label="Materialtyp" sort={sort} order={order} onSort={onSort} />
                <SortableHeader column="created_at" label="Erstellt am" sort={sort} order={order} onSort={onSort} />
                {isKalotte && <TableCell align="right">Aktionen</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                  <TableCell>
                    {item.material_type === 'EDELSTAHL'
                      ? 'Edelstahl'
                      : item.material_type === 'ALU'
                        ? 'Aluminium'
                        : item.material_type === 'HSF_PP'
                          ? 'HSF-PP'
                          : item.material_type === 'HSF_PE'
                            ? 'HSF-PE'
                            : item.material_type}
                  </TableCell>
                  <TableCell>{new Date(item.created_at).toLocaleDateString('de-DE')}</TableCell>
                  {isKalotte && (
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenDialog('edit', item)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(item)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Neue Werkstoffnorm' : 'Werkstoffnorm bearbeiten'}
        </DialogTitle>
        <DialogContent>
          <TextField
            required
            fullWidth
            label="Name"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Materialtyp</InputLabel>
            <Select
              value={formMaterialType}
              label="Materialtyp"
              onChange={(e) => setFormMaterialType(e.target.value as MaterialType)}
            >
              <MenuItem value="EDELSTAHL">Edelstahl</MenuItem>
              <MenuItem value="ALU">Aluminium</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formName.trim()}>
            {dialogMode === 'create' ? 'Erstellen' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaterialNormsTable;
